import React from "react";
import { useNavigate } from "react-router-dom";
import { Eye, MessageCircle, BookOpen } from "lucide-react";
import AstroMarkdown from "./AstroMarkdown";
import AccuracyVote from "./AccuracyVote";
import { useI18n } from "../contexts/I18nContext";

// "Your nature, and these recent months" — the read that earns trust. It never
// blocks the report: a slim shimmer holds the spot, then the card arrives.
export const NatureReadCard = ({ deep, reportId }) => {
  const { lang } = useI18n();
  const navigate = useNavigate();
  if (!deep || (!deep.loading && !deep.answer)) return null;

  const heading = lang === "hi" ? "आपका स्वभाव और हाल के महीने" : "Your nature, and these recent months";

  if (deep.loading) {
    return (
      <div data-testid="nature-read-loading" className="glass-card" style={{
        marginBottom: "18px", padding: "15px 17px",
        border: "1px solid rgba(192,132,252,0.26)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
          <Eye size={14} style={{ color: "#c084fc" }} />
          <span style={{ color: "#c084fc", fontWeight: 700, fontSize: "0.86rem" }}>{heading}</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "9px" }}>
          {["94%", "100%", "82%"].map((w, i) => (
            <div key={i} className="skeleton" style={{ height: "11px", width: w, borderRadius: "6px" }} />
          ))}
        </div>
        <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.78rem", margin: "11px 0 0" }}>
          {lang === "hi"
            ? "कई संकेत मिलाकर पढ़ रहे हैं — आपकी रिपोर्ट नीचे तैयार है।"
            : "Cross-checking a few signals in your chart — your full report is ready below."}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="nature-read-card" className="animate-fadeInUp" style={{
      marginBottom: "18px", padding: "2px", borderRadius: "18px",
      background: "linear-gradient(135deg, rgba(192,132,252,0.55), rgba(255,209,102,0.45))",
    }}>
      <div style={{ background: "#160126", borderRadius: "16px", padding: "17px 19px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
          <Eye size={15} style={{ color: "#c084fc" }} />
          <span className="font-cinzel" style={{ color: "#c084fc", fontSize: "0.98rem" }}>{heading}</span>
        </div>
        <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.75rem", margin: "0 0 12px" }}>
          {lang === "hi"
            ? "केवल वही जहाँ कुंडली के कई संकेत सहमत हैं"
            : "Only what several signals in your chart agree on"}
        </p>

        <div data-testid="nature-read-text" style={{ fontSize: "0.92rem" }}>
          <AstroMarkdown>{deep.answer}</AstroMarkdown>
        </div>

        <div data-testid="nature-read-vote" style={{ marginTop: "13px" }}>
          <AccuracyVote kind="nature" reportId={reportId} compact label="This was spot on" />
        </div>

        <div style={{ display: "flex", gap: "9px", flexWrap: "wrap", marginTop: "14px" }}>
          <button data-testid="nature-read-ask-btn" className="btn-gold"
            onClick={() => navigate("/chat")}
            style={{ padding: "10px 18px", fontSize: "0.84rem" }}>
            <MessageCircle size={13} style={{ display: "inline", marginRight: "7px", verticalAlign: "-2px" }} />
            {lang === "hi" ? "अपना सवाल पूछें — मुफ़्त" : "Ask your own question — free"}
          </button>
          <button data-testid="nature-read-report-btn" className="btn-ghost"
            onClick={() => {
              const el = document.querySelector("[data-testid='kundli-tabs']");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            style={{ padding: "10px 18px", fontSize: "0.84rem" }}>
            <BookOpen size={13} style={{ display: "inline", marginRight: "7px", verticalAlign: "-2px" }} />
            {lang === "hi" ? "पूरी विस्तृत रिपोर्ट" : "Read my full detailed report"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NatureReadCard;
